import { useMarketingSession } from '@/features/marketing/use-marketing-session';
import { Outlet, createFileRoute, useLocation } from '@tanstack/react-router';
import { canAccessMarketingPath, isMarketingPathPending, marketingPathRefusal, useMarketingAccess, type MarketingRefusal } from '@/features/marketing/use-marketing-access';
import { Helmet } from 'react-helmet-async';
// Module files, not the `@/features/marketing` barrel: this layout is in every Marketing page's first download.
import { MarketingPageShell } from '@/features/marketing/marketing-page-shell';
import { MarketingPageState } from '@/features/marketing/marketing-page-state';
import { MarketingShellSkeleton } from '@/features/marketing/marketing-shell-skeleton';
import { MarketingWorkspaceLayout } from '@/features/marketing/nav/marketing-workspace-layout';
import { ListingsOffRefusal } from '@/features/marketing/listings/listings-off-refusal';
import { formatDocumentTitle } from '@/lib/document-title';

/** What a refused Marketing URL says: never blame permissions for a page this salon simply doesn't use. */
const REFUSAL_COPY: Record<MarketingRefusal, { title: string; description: string }> = {
  NO_ACCESS: {
    title: 'This Marketing action is not available',
    description: 'Ask your salon admin for the required marketing and client access permissions.',
  },
  SALON_FACTS_FAILED: { title: "Couldn't load this page", description: 'Check your connection and reload the page.' },
  HAYVEN_TAP_NOT_USED: {
    title: 'Hayven Tap isn’t set up here',
    description: 'Clients use their gifts at check-in, and there’s no Hayven Tap stand here yet. Your Hayven agency sets up stands.',
  },
  LISTINGS_OFF: {
    title: 'Listings isn’t on here',
    description: 'Listings sends your salon info to Apple Maps, Bing, Yelp and more. Your Hayven agency turns it on.',
  },
};

export const Route = createFileRoute('/_auth/$orgId/marketing')({
  component: MarketingLayout,
});

/**
 * Marketing module layout. Verify the operation before mounting its page and queries; every allowed or
 * refused Marketing URL renders inside the sidebar frame.
 */
function MarketingLayout() {
  const { orgId } = Route.useParams();
  const access = useMarketingAccess();
  const sessionReady = useMarketingSession();
  const pathname = useLocation({ select: (location) => location.pathname });

  // Static frame placeholder (no queries) until access + session resolve — nothing protected mounts early. A page whose
  // rule reads the salon facts (Hayven Tap, Listings) also waits for them, so a direct URL is never refused while they load.
  if (access.isLoading || !sessionReady || isMarketingPathPending(access, pathname)) return <MarketingShellSkeleton pathname={pathname} />;

  const refusal = canAccessMarketingPath(access, pathname) ? null : marketingPathRefusal(access, pathname);

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Marketing')}</title>
      </Helmet>
      <MarketingWorkspaceLayout orgId={orgId}>
        {refusal === null ? (
          <Outlet key={access.revision} />
        ) : refusal === 'LISTINGS_OFF' ? (
          // The salon settings may predate the agency turning Listings on: re-read them before saying it's off.
          <ListingsOffRefusal orgId={orgId} {...REFUSAL_COPY.LISTINGS_OFF} />
        ) : (
          <MarketingPageShell orgId={orgId} title="Marketing">
            <MarketingPageState {...REFUSAL_COPY[refusal]} />
          </MarketingPageShell>
        )}
      </MarketingWorkspaceLayout>
    </>
  );
}
